// @ts-check

import { infiniteQueryOptions, mutationOptions } from "@tanstack/react-query"
import { addChatMessageFeedback, editChatMessage, generateChatMessage, getChatSessionMessages, regenerateChatMessage, sendAgentQuery } from "../api"
import { handleStream, setStreamQueriesData } from "../streams"

const PAGE_SIZE = 15

const ChatMessageQueryKeys = {
  /** @param {string} sessionId */
  CHAT_MESSAGES: (sessionId) => ["messages", sessionId],
}

/** @typedef {import("@tanstack/react-query").InfiniteData<{ messages: ChatMessage[], totalCount: number, hasMore: boolean }, number>} ChatMessagesQueryData */

const ChatMessageQueryActions = {
  /**
   * Append committed messages to the last page (called after stream ends).
   * @param {ChatMessage[]} newMessages
   */
  APPEND_MESSAGES(newMessages) {
    /** @param {ChatMessagesQueryData} data */
    return (data) => {
      if (data == null || newMessages.length === 0) return data
      const pages = [...data.pages]
      const last = pages.length - 1
      pages[last] = {
        ...pages[last],
        messages: [...pages[last].messages, ...newMessages],
        totalCount: pages[last].totalCount + newMessages.length,
      }
      return { ...data, pages }
    }
  },

  /**
   * Update a single message across all pages (edit / regenerate / feedback).
   * @param {string} messageId
   * @param {Partial<ChatMessage>} updated
   */
  UPDATE_MESSAGE(messageId, updated) {
    /** @param {ChatMessagesQueryData} data */
    return (data) => {
      if (data == null) return data
      return {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          messages: page.messages.map(msg =>
            msg._id === messageId ? { ...msg, ...updated } : msg
          ),
        })),
      }
    }
  },

  /**
   * Remove all messages from a given message ID onward (optimistic edit/regenerate).
   * Returns the removed messages so they can be restored on error.
   * @param {string} fromMessageId - ID of the message to truncate from (inclusive)
   */
  TRUNCATE_FROM(fromMessageId) {
    /** @param {ChatMessagesQueryData} data */
    return (data) => {
      if (data == null) return { data, removed: [] }
      let removed = /** @type {ChatMessage[]} */ ([])
      const pages = data.pages.map(page => {
        const idx = page.messages.findIndex(m => m._id === fromMessageId)
        if (idx === -1) return page
        removed = [...removed, ...page.messages.slice(idx)]
        return {
          ...page,
          messages: page.messages.slice(0, idx),
          totalCount: page.totalCount - (page.messages.length - idx),
        }
      })
      return { data: { ...data, pages }, removed }
    }
  },

  /**
   * Restore messages that were removed by TRUNCATE_FROM (used in onError rollback).
   * @param {ChatMessage[]} messages
   */
  RESTORE_MESSAGES(messages) {
    /** @param {ChatMessagesQueryData} data */
    return (data) => {
      if (data == null || messages.length === 0) return data
      const pages = [...data.pages]
      const last = pages.length - 1
      pages[last] = {
        ...pages[last],
        messages: [...pages[last].messages, ...messages],
        totalCount: pages[last].totalCount + messages.length,
      }
      return { ...data, pages }
    }
  },
}

const ChatMessageQueryOptions = {
  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.userToken
   */
  // @ts-ignore — select changing the return type from InfiniteData to a flat array is valid at runtime but TS overloads don't allow it
  CHAT_MESSAGES: ({ sessionId, userToken }) => infiniteQueryOptions({
    queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId),
    queryFn: ({ pageParam, signal }) =>
      getChatSessionMessages(sessionId, userToken, { offset: pageParam, limit: PAGE_SIZE, signal }),
    initialPageParam: 0,
    getNextPageParam: () => undefined,
    // Each fetchPreviousPage call loads the next older batch.
    // React Query prepends the result to data.pages, so flatMap gives chronological order.
    getPreviousPageParam: (firstPage, allPages) =>
      firstPage.hasMore ? allPages.length * PAGE_SIZE : undefined,
    select: (data) => data.pages.flatMap(page => page.messages),
    staleTime: Infinity,
    enabled: userToken != null && sessionId != null,
  }),
}

const ChatMessageMutationOptions = {
  /**
   * @param {Object} params
   * @param {Object|null} params.user
   * @param {string} params.country
   * @param {number} params.temperature
   * @param {string} params.systemPrompt
   * @param {number} params.topP
   * @param {() => string} params.getUserToken
   * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
   */
  SEND_MESSAGE: ({ user, country, temperature, systemPrompt, topP, getUserToken, onStreamEvent }) => mutationOptions({
  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.text
   * @param {ChatFile[]} [params.files]
   * @param {ChatImage[]} [params.images]
   * @param {string} [params.voiceChatSystemPrompt]
   */
    mutationFn: ({ sessionId, text, files, images, voiceChatSystemPrompt }) => {
      const messageData = {
        historyId: sessionId,
        text,
        user: user?._id,
        files: files?.length ? files.map(f => ({ name: f.name, content: f.content })) : undefined,
        images: images?.length ? images.map(img => ({ _id: img._id, _clientId: img._clientId })).filter(img => img._id != null) : undefined,
        country: country ?? "",
        role: user?.role,
        workerType: user?.workerType,
        // ! Remove later
        // ! Used for testing only
        // @ts-ignore
        temperature: Math.max(temperature, 0.001),
        systemPrompt: voiceChatSystemPrompt || systemPrompt, // 🎤 Use voice chat prompt if provided
        topP,
      }

      const readable = generateChatMessage(sessionId, messageData, getUserToken())
      return handleStream(readable, {
        sessionId,
        getUserToken,
        onStreamEvent,
      })
    },
    async onSuccess(data, variables, _, context) {
      await setStreamQueriesData(context.client, data, variables.sessionId)
    },
  }),

  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {Object|null} params.user
   * @param {string} params.country
   * @param {number} params.temperature
   * @param {string} params.systemPrompt
   * @param {number} params.topP
   * @param {() => string} params.getUserToken
   * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
   */
  EDIT_MESSAGE: ({ sessionId, user, country, temperature, systemPrompt, topP, getUserToken, onStreamEvent }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.messageId
     * @param {string} params.text
     */
    mutationFn: ({ messageId, text }) => {
      const messageData = {
        historyId: sessionId,
        messageId,
        text,
        user: user?._id,
        country: country ?? "",
        role: user?.role,
        workerType: user?.workerType,
        temperature: Math.max(temperature, 0.001),
        systemPrompt,
        topP,
      }

      const readable = editChatMessage(sessionId, messageId, messageData, getUserToken())
      return handleStream(readable, {
        sessionId,
        getUserToken,
        onStreamEvent,
      })
    },
    async onMutate(variables, context) {
      await context.client.cancelQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) })

      const currentData = context.client.getQueryData(ChatMessageQueryKeys.CHAT_MESSAGES(sessionId))
      if (currentData == null) return { removedMessages: [] }

      const { data: newData, removed } = ChatMessageQueryActions.TRUNCATE_FROM(variables.messageId)(currentData)
      context.client.setQueryData(ChatMessageQueryKeys.CHAT_MESSAGES(sessionId), newData)

      return { removedMessages: removed }
    },
    async onSuccess(data, variables, mutateResult, context) {
      await setStreamQueriesData(context.client, data, sessionId)
    },
    async onError(error, variables, mutateResult, context) {
      await context.client.cancelQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) })

      const removedMessages = mutateResult?.removedMessages ?? []
      if (removedMessages.length <= 0) return

      context.client.setQueryData(
        ChatMessageQueryKeys.CHAT_MESSAGES(sessionId),
        ChatMessageQueryActions.RESTORE_MESSAGES(removedMessages)
      )
    },
  }),

  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {Object|null} params.user
   * @param {string} params.country
   * @param {number} params.temperature
   * @param {string} params.systemPrompt
   * @param {number} params.topP
   * @param {() => string} params.getUserToken
   * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
   */
  REGENERATE_MESSAGE: ({ sessionId, user, country, temperature, systemPrompt, topP, getUserToken, onStreamEvent }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.messageId
     */
    mutationFn: ({ messageId }) => {
      const messageData = {
        historyId: sessionId,
        messageId,
        country: country ?? "",
        role: user?.role,
        workerType: user?.workerType,
        temperature: Math.max(temperature, 0.001),
        systemPrompt,
        topP,
      }

      const readable = regenerateChatMessage(sessionId, messageId, messageData, getUserToken())
      return handleStream(readable, {
        sessionId,
        getUserToken,
        onStreamEvent,
      })
    },
    async onMutate(variables, context) {
      await context.client.cancelQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) })

      const currentData = context.client.getQueryData(ChatMessageQueryKeys.CHAT_MESSAGES(sessionId))
      if (currentData == null) return { removedMessages: [] }

      const { data: newData, removed } = ChatMessageQueryActions.TRUNCATE_FROM(variables.messageId)(currentData)
      context.client.setQueryData(ChatMessageQueryKeys.CHAT_MESSAGES(sessionId), newData)

      return { removedMessages: removed }
    },
    async onSuccess(data, variables, mutateResult, context) {
      await setStreamQueriesData(context.client, data, sessionId)
    },
    async onError(error, variables, mutateResult, context) {
      await context.client.cancelQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) })

      const removedMessages = mutateResult?.removedMessages ?? []
      if (removedMessages.length <= 0) return

      context.client.setQueryData(
        ChatMessageQueryKeys.CHAT_MESSAGES(sessionId),
        ChatMessageQueryActions.RESTORE_MESSAGES(removedMessages)
      )
    },
  }),

  /**
   * @param {Object} params
   * @param {Object|null} params.user
   * @param {string} params.country
   * @param {number} params.temperature
   * @param {string} params.systemPrompt
   * @param {number} params.topP
   * @param {() => string} params.getUserToken
   * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
   */
  SEND_AGENT_MESSAGE: ({ user, country, temperature, systemPrompt, topP, getUserToken, onStreamEvent }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.sessionId
     * @param {string} params.query
     * @param {ChatImage[]} [params.images]
     * @param {ChatFile[]} [params.files]
     * @param {import("../utils").AgentModeValue} params.mode
     * @param {import("../utils").AgentModeValue} [params.forceAgentMode]
     * @param {string} [params.canvasDocuments]
     */
    mutationFn: ({ sessionId, query, images, files, mode, forceAgentMode, canvasDocuments }) => {
      const messageData = {
        query,
        userId: user?._id,
        images: images?.length ? images.map(img => img._id).filter(Boolean) : undefined,
        files: files?.length ? files.map(f => ({ name: f.name, content: f.content })) : undefined,
        mode,
        canvasDocuments,
        country: country ?? "",
        role: user?.role,
        workerType: user?.workerType,
        temperature: Math.max(temperature, 0.001),
        systemPrompt,
        topP,
      }

      const readable = sendAgentQuery(sessionId, messageData, getUserToken())
      return handleStream(readable, {
        sessionId,
        getUserToken,
        onStreamEvent,
      })
    },
    async onSuccess(data, variables, mutateResult, context) {
      await setStreamQueriesData(context.client, data, variables.sessionId)
    },
  }),

  /**
   * @param {Object} params
     * @param {string} params.sessionId
   * @param {string} [params.userToken]
   */
  SEND_FEEDBACK: ({ sessionId, userToken }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {ChatFeedbackData} params.data
     * @param {number} params.chatIndex
     */
    mutationFn: ({ data, chatIndex }, context) => {
      const feedbackData = {
        historyId: sessionId,
        chatIndex,
        ...data,
      }
      // Look up the message _id from the paginated messages cache by flat index.
      const messagesData = context.client.getQueryData(ChatMessageQueryKeys.CHAT_MESSAGES(sessionId))
      const allMessages = messagesData?.pages?.flatMap(page => page.messages) ?? []
      const messageId = allMessages[chatIndex]?._id

      return addChatMessageFeedback(sessionId, messageId, feedbackData, userToken)
    },
    async onMutate(variables, context) {
      await context.client.cancelQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) })

      const messagesData = context.client.getQueryData(ChatMessageQueryKeys.CHAT_MESSAGES(sessionId))
      const allMessages = messagesData?.pages?.flatMap(page => page.messages) ?? []
      const targetMessage = allMessages[variables.chatIndex]
      if (targetMessage == null) return {}

      context.client.setQueryData(
        ChatMessageQueryKeys.CHAT_MESSAGES(sessionId),
        ChatMessageQueryActions.UPDATE_MESSAGE(targetMessage._id, {
          ...variables.data,
          ...(variables.data.isGood ? { isBad: false } : null),
          ...(variables.data.isBad ? { isGood: false } : null),
        })
      )

      return { previousMessageId: targetMessage._id, previousData: targetMessage }
    },
    async onSuccess(data, variables, mutateResult, context) {
      // Server returns the full updated session — sync each updated message back.
      // For now, the optimistic update is sufficient; a full refresh would
      // reset pagination state, so we leave the cache as-is.
    },
    onError(error, variables, mutateResult, context) {
      if (mutateResult?.previousMessageId == null) return
      context.client.setQueryData(
        ChatMessageQueryKeys.CHAT_MESSAGES(sessionId),
        ChatMessageQueryActions.UPDATE_MESSAGE(mutateResult.previousMessageId, mutateResult.previousData)
      )
    },
  })
}

export {
  ChatMessageQueryKeys,
  ChatMessageQueryActions,
  ChatMessageQueryOptions,
  ChatMessageMutationOptions,
}
